import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { items } from "../../data/items.ts";
import LoadingModal from "../Common/LoadingModal";
import ModalCloseButton from "../Common/ModalCloseButton";
import "./GunPriceModal.css";

const GunPriceModal = ({ show, onClose, onSave }) => {
  const [guns, setGuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      fetchGunPrices();
    }
  }, [show]);

  const fetchGunPrices = async () => {
    setLoading(true);
    try {
      const storedAuth = localStorage.getItem("uploadAuth");
      if (!storedAuth) {
        throw new Error("No auth token found");
      }
      
      const { guid } = JSON.parse(storedAuth);
      // Get all gun IDs from items.ts
      const gunIds = items.map(item => item.id);
      
      const res = await fetch(
        "https://dat-photo-admin.t9e2i3kal.workers.dev/pricing/batch-get",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${guid}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ids: gunIds
          }),
        }
      );
      
      if (!res.ok) throw new Error("Failed to fetch gun prices");
      const { data } = await res.json();
      
      // Merge gun data with prices from API
      const mergedGuns = items.map(gun => ({
        ...gun,
        price: data[gun.id] || gun.price
      }));
      
      setGuns(mergedGuns);
    } catch (err) {
      console.error("Failed to fetch gun prices:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (id, newPrice) => {
    setGuns(prev => prev.map(gun => 
      gun.id === id ? { ...gun, price: newPrice } : gun
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const storedAuth = localStorage.getItem("uploadAuth");
      if (!storedAuth) {
        throw new Error("No auth token found");
      }
      
      const { guid } = JSON.parse(storedAuth);
      const pricing = {};
      
      guns.forEach(gun => {
        pricing[gun.id] = gun.price;
      });
      
      const res = await fetch(
        "https://dat-photo-admin.t9e2i3kal.workers.dev/pricing",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${guid}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pricing
          }),
        }
      );
      
      if (!res.ok) throw new Error("Failed to update gun prices");
      onSave();
      onClose();
    } catch (err) {
      console.error("Failed to update gun prices:", err);
    } finally {
      setSaving(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  
  const filteredGuns = guns.filter(gun => 
    gun.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    show && (
      <div className="gun-price-modal-overlay">
        {(loading || saving) && (
          <LoadingModal message={loading ? "Loading gun prices..." : "Saving price updates..."} />
        )}
        <div className="gun-price-modal">
          <ModalCloseButton onClick={onClose} size="small" className="close-btn" />
          <h2>Update Gun Prices</h2>
          <h2 >
            Note: Setting price to $0 will disable this item from the website
          </h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search guns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          {loading ? null : (
            <div className="gun-price-list">
              {filteredGuns.map((gun) => (
                <div key={gun.id} className="gun-price-card">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {gun.images?.[0] && (
                      <img 
                        src={gun.images[0]} 
                        className="gun-thumbnail" 
                        alt={gun.title}
                      />
                    )}
                    <div className="gun-title">{gun.title}</div>
                  </div>
                  <div className="price-input-group">
                    <label>Price ($)</label>
                    <input
                      type="text"
                      value={gun.price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        handlePriceChange(gun.id, value === "" ? 0 : parseInt(value, 10));
                      }}
                      onKeyDown={(e) => {
                        if (
                          !/\d/.test(e.key) &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                      }}
                      className="price-input"
                      inputMode="numeric"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="modal-buttons">
            <button
              className="btn btn-success"
              onClick={() => handleSave()}
              disabled={loading || saving}
            >
              {saving ? "Updating..." : "Update All Prices"}
            </button>
            <button
              className="btn btn-danger"
              onClick={onClose}
              disabled={loading || saving}
            >
              <X size={16} /> Close
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default GunPriceModal;
